<template>
  <Dialog
    :visible="visible"
    modal
    header="Ads.txt file validation result"
    :closable="false"
    style="width: 44rem; max-width: 92vw"
  >
    <div v-if="parsedLines.length || fetchFailed || partnerStatusArray.length">
      <p>
        <b>{{
          adstxtVerified ? "Validation passed." : "Validation failed."
        }}</b>
        {{
          lastChecked?.length
            ? `Last checked: ${new Date(lastChecked).toLocaleString()}`
            : ""
        }}
      </p>

      <Message v-if="fetchFailed" severity="error" :closable="false">
        The ads.txt file could not be fetched from this domain.
      </Message>

      <div v-if="parsedLines.length">
        <p>
          You need to add missing lines (below) to your current ads.txt file.
        </p>

        <div v-for="group in priorityGroups" :key="group.priority" class="mb-3">
          <div class="flex align-items-center gap-2 mb-1">
            <Tag :severity="group.severity" :value="group.label" />
            <span class="text-500 text-sm"
              >{{ group.lines.length }} missing</span
            >
          </div>
          <div
            v-for="line in group.lines"
            :key="line.row"
            class="flex align-items-center flex-wrap gap-1 mb-1"
          >
            <Tag
              v-if="line.partner"
              severity="secondary"
              :value="line.partner"
            />
            <Tag
              v-if="line.type"
              severity="secondary"
              :value="line.type.toUpperCase()"
            />
            <code class="line-code">{{ line.row }}</code>
          </div>
        </div>

        <div v-if="mismatchLines.length" class="mb-3">
          <div class="flex align-items-center gap-2 mb-1">
            <Tag severity="info" value="Wrong relationship" />
            <span class="text-500 text-sm"
              >{{ mismatchLines.length }} lines</span
            >
          </div>
          <div
            v-for="line in mismatchLines"
            :key="line.row"
            class="flex align-items-center flex-wrap gap-1 mb-1"
          >
            <Tag
              v-if="line.partner"
              severity="secondary"
              :value="line.partner"
            />
            <code class="line-code">{{ line.row }}</code>
            <span class="text-sm text-500">
              listed as {{ line.found }}, should be {{ line.expected }}
            </span>
          </div>
        </div>

        <div
          class="surface-50 surface-border text-color-secondary border-1 overflow-x-hidden border-round-md p-3 mb-2 relative"
        >
          <Button
            @click="codeToClipboard()"
            icon="pi pi-copy"
            text
            aria-label="Copy"
            severity="secondary"
            v-tooltip.bottom="'Copy'"
            class="absolute right-0 top-0 embed-code-copy-btn"
          />
          <pre ref="adstxtRef">{{ adstxtContent }}</pre>
        </div>
      </div>

      <div v-if="partnerStatusArray.length" class="mt-4">
        <h3>Partner Status</h3>
        <div
          v-for="partner in partnerStatusArray"
          :key="partner.name"
          class="mb-2"
        >
          <div class="flex align-items-center">
            <Badge
              :severity="partner.status === 'GREEN' ? 'success' : 'danger'"
              class="mr-2"
            />
            <span class="font-bold">{{ partner.name }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-lg">
      Verification not done yet. Check back later.
    </div>
    <template #footer>
      <Button
        label="Close"
        outlined
        icon="pi pi-check"
        @click="emits('close-modal')"
        autofocus
      />
    </template>
  </Dialog>
</template>

<script setup>
import Button from "primevue/button";
import Badge from "primevue/badge";
import Tag from "primevue/tag";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import { computed, ref } from "vue";

const props = defineProps({
  visible: Boolean,
  missingLines: String,
  lastChecked: String,
  adsTxtPerDemand: [String, Object],
});

const emits = defineEmits(["close-modal"]);
const adstxtRef = ref(null);

const codeToClipboard = () => {
  let clipboardData = adstxtRef.value.innerText;
  navigator.clipboard.writeText(clipboardData);
};

// Supports both the legacy format (array of strings) and the structured
// format (array of {row, partner, type, priority})
const parsedResult = computed(() => {
  const result = { lines: [], fetchFailed: false };
  if (!props.missingLines?.length) return result;
  try {
    const parsed = JSON.parse(props.missingLines);
    if (!Array.isArray(parsed)) return result;
    for (const item of parsed) {
      if (item && typeof item === "object" && item.row) {
        result.lines.push({
          row: item.row,
          partner: item.partner || "",
          type: item.type || "",
          priority: Number(item.priority) || 3,
          ...(item.mismatch && {
            mismatch: true,
            expected: item.expected,
            found: item.found,
          }),
        });
      } else if (typeof item === "string") {
        if (
          item.includes("ads.txt file could not be fetched") ||
          item.includes("Error during validation")
        ) {
          result.fetchFailed = true;
        } else {
          result.lines.push({ row: item, partner: "", type: "", priority: 0 });
        }
      }
    }
  } catch (e) {
    console.error("Error parsing adstxt_missing_lines:", e);
  }
  return result;
});

const parsedLines = computed(() => parsedResult.value.lines);
const fetchFailed = computed(() => parsedResult.value.fetchFailed);

const priorityMeta = {
  0: { label: "Missing", severity: "danger" },
  1: { label: "P1 — Critical", severity: "danger" },
  2: { label: "P2 — High", severity: "warning" },
  3: { label: "P3 — Low", severity: "secondary" },
};

const priorityGroups = computed(() => {
  const groups = [];
  for (const priority of [0, 1, 2, 3]) {
    const lines = parsedLines.value.filter(
      (line) =>
        !line.mismatch && (line.priority >= 3 ? 3 : line.priority) === priority
    );
    if (lines.length) {
      groups.push({ priority, ...priorityMeta[priority], lines });
    }
  }
  return groups;
});

const mismatchLines = computed(() =>
  parsedLines.value.filter((line) => line.mismatch)
);

const adstxtContent = computed(() => {
  return parsedLines.value.map((line) => line.row).join("\n");
});

const partnerStatusArray = computed(() => {
  try {
    const parsedData =
      typeof props.adsTxtPerDemand === "string"
        ? JSON.parse(props.adsTxtPerDemand)
        : props.adsTxtPerDemand;

    if (!parsedData) return [];

    return Object.entries(parsedData).map(([name, status]) => ({
      name,
      status,
    }));
  } catch (e) {
    console.error("Error parsing ads_txt_per_demand:", e);
    return [];
  }
});

const adstxtVerified = computed(() => {
  return (
    !fetchFailed.value &&
    !parsedLines.value.length &&
    partnerStatusArray.value.every((partner) => partner.status === "GREEN")
  );
});
</script>

<style scoped>
.embed-code-copy-btn {
  opacity: 0.5;
  transition: opacity 0.3s;
}
.embed-code-copy-btn:hover {
  opacity: 1;
}
.line-code {
  font-family: "Courier New", monospace;
  font-size: 0.85rem;
  word-break: break-all;
}
</style>
